import React, { useCallback, useRef, useState } from "react"
import { useClickOutside } from "@ciq/shared/hooks/useClickOutside"
import { STATUS_CONFIG, STATUS_OPTIONS, PLACEMENT_STATUS } from "@ciq/shared/pages/file_upload/constants"

/**
 * Status pill with colored dot and dropdown for editing placement status.
 *
 * @param {Object} props
 * @param {string} props.status - Current placement status (pending, placed, out_of_scope)
 * @param {Function} props.onStatusChange - Callback when status changes, receives new status string
 * @param {boolean} [props.readOnly] - Boolean indicating if the dropdown is disabled
 * @returns {React.ReactElement} The status pill component
 */
export function StatusPill({ status, onStatusChange, readOnly }) {
  const [isOpen, setIsOpen] = useState(false)
  const pillRef = useRef(null)
  const buttonRef = useRef(null)

  const config = STATUS_CONFIG[status] || STATUS_CONFIG[PLACEMENT_STATUS.PENDING]

  const closeDropdown = useCallback(() => setIsOpen(false), [])
  useClickOutside(pillRef, isOpen, closeDropdown)

  const handleToggle = useCallback(() => {
    if (readOnly) return
    setIsOpen(prev => !prev)
  }, [readOnly])

  const handleSelect = useCallback((newStatus) => {
    if (newStatus !== status) {
      onStatusChange(newStatus)
    }
    setIsOpen(false)
  }, [status, onStatusChange])

  const handleDropdownKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      setIsOpen(false)
      buttonRef.current?.focus()
      return
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      const options = pillRef.current?.querySelectorAll("[role='option']")
      if (!options?.length) return
      const focused = document.activeElement
      const idx = Array.from(options).indexOf(focused)
      const next = e.key === "ArrowDown"
        ? (idx + 1) % options.length
        : (idx - 1 + options.length) % options.length
      options[next]?.focus()
    }
  }, [])

  return (
    <div className="status-pill-container" ref={pillRef}>
      <button
        ref={buttonRef}
        className="status-pill"
        onClick={handleToggle}
        aria-haspopup={readOnly ? undefined : "listbox"}
        aria-expanded={readOnly ? undefined : isOpen}
        aria-disabled={readOnly || undefined}
        type="button"
      >
        <span className={`status-pill-dot ${config.colorClass}`} />
        <span className="status-pill-label">{config.label}</span>
      </button>
      {isOpen && (
        <div
          className="status-pill-dropdown"
          role="listbox"
          aria-label="Change status"
          onKeyDown={handleDropdownKeyDown}
        >
          {STATUS_OPTIONS.map(option => (
            <div
              key={option.value}
              className={`status-pill-option${option.value === status ? " selected" : ""}`}
              role="option"
              aria-selected={option.value === status}
              tabIndex={0}
              onClick={() => handleSelect(option.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(option.value) } }}
            >
              <span className={`status-pill-dot ${STATUS_CONFIG[option.value].colorClass}`} />
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
